import { useEffect, useRef, useCallback } from 'react';

/**
 * Visibility-aware interval hook. Pauses when the browser tab is hidden
 * and resumes (with an immediate call) when the tab becomes visible again.
 *
 * @param {Function} callback - Function to call on each interval tick
 * @param {number} delay - Interval in milliseconds
 * @param {boolean} [enabled=true] - Whether the interval should be active
 */
export default function useSmartInterval(callback, delay, enabled = true) {
  const savedCallback = useRef(callback);
  const intervalRef = useRef(null);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  const start = useCallback(() => {
    if (intervalRef.current) return;
    intervalRef.current = setInterval(() => {
      savedCallback.current();
    }, delay);
  }, [delay]);

  const stop = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      stop();
      return;
    }

    savedCallback.current();
    start();

    const handleVisibilityChange = () => {
      if (document.hidden) {
        stop();
      } else {
        savedCallback.current();
        start();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [delay, enabled, start, stop]);
}
